'use client'

import {
  createContext,
  useContext,
  useMemo,
  useState,
  type MouseEvent,
} from 'react'

import { cn, setCookie } from '@/shared/lib'
import { Icon } from '@/shared/ui'

import { BANNER_COOKIE_VALUE, getExpireTime } from '../../lib/slim-banner'

type BannerState = {
  isVisible: boolean
  close: () => void
}

const BannerContext = createContext<BannerState | null>(null)

export const useBannerContext = () => {
  const context = useContext(BannerContext)

  if (!context) {
    throw new Error('useBannerContext must be used within a BannerProvider')
  }

  return context
}

type Props = {
  cookieName: string
  initialVisible: boolean
  className?: string
  children: React.ReactNode
}

export const Root = ({
  cookieName,
  initialVisible,
  className,
  children,
}: Props) => {
  const [isVisible, setIsVisible] = useState(initialVisible)

  const handleClose = () => {
    setIsVisible(false)
    setCookie(cookieName, BANNER_COOKIE_VALUE, {
      expires: getExpireTime(),
    })
  }

  const value = useMemo(() => ({ isVisible, close: handleClose }), [isVisible])

  return (
    <BannerContext.Provider value={value}>
      {isVisible && (
        <section className={cn('relative flex h-12 bg-gray-900', className)}>
          {children}
        </section>
      )}
    </BannerContext.Provider>
  )
}

type CloseProps = {
  className?: string
}

export const Close = ({ className }: CloseProps) => {
  const { close } = useBannerContext()

  const handleClick = (event: MouseEvent) => {
    event.preventDefault()
    event.stopPropagation()
    close()
  }

  return (
    <button
      className={cn(
        'absolute top-1/2 right-4 -translate-y-1/2',
        'flex h-8 w-8 cursor-pointer items-center justify-center',
        className,
      )}
      onClick={handleClick}
      aria-label="하루동안 배너를 표시하지 않음"
    >
      <Icon name="close" size={16} className="text-white" />
    </button>
  )
}
